import { LoadingButton } from "@mui/lab";
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, TextField, Stack, Card, CardActions, CardContent, CardHeader, Typography, IconButton, Tooltip } from "@mui/material";
import { useContext, useState } from "react";
import { useSearchResidentHttp } from "../../hooks/useSearchResidentHttp";
import { Asset } from "../../data/Asset";
import { ResidentDto } from "../../dtos/ResidentDto";
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import BadgeIcon from '@mui/icons-material/Badge';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import VspPortalContext from "../../store/VspPortalContext";
import { NotificationTypes } from "../../enums/NotificationTypes";
import { VspPortalActions } from "../../store/VspPortalActions";


type SearchBhpEmployeeContractorProps = {

    assets: Asset[ ];
    onSelectResident( resident: ResidentDto | undefined ): void;

};


const dataGridSx = {

    cursor: 'pointer',
    '.MuiDataGrid-columnHeaderTitle': { 
        fontWeight: 'bold !important',
        overflow: 'visible !important'
    },

    '.MuiDataGrid-cell:focus': {
        outline: 'none'
    }

};


const textFieldSx = {
    
    width: '250px'

};


const columns: GridColDef[ ] = [ 

    {
        field: 'firstName',
        headerName: 'First Name',
        width: 250,
    },
    
    {
        field: 'surname',
        headerName: 'Last Name',
        width: 250,
    },

    {
        field: 'employeeId',
        headerName: 'Employee Id',
        width: 250,
    },

    {
        field: 'cardNumbers',
        headerName: 'Card Number(s)',
        width: 471,
        renderCell: ( params => params.row.cardNumbers.join(', ') ),
        // sortable: false
    }

];


export const SearchBhpEmployeeContractor : React.FC< SearchBhpEmployeeContractorProps > = ( props : SearchBhpEmployeeContractorProps ) => {

    const { assets, onSelectResident } = props;
    
    const [ asset, setAsset ] = useState< Asset >( Asset.Waio );
    const [ buttonText, setButtonText ] = useState< string >( 'Search' );
    const [ canSearch, setCanSearch ] = useState<boolean>( false );
    const [ cardNumber, setCardNumber ] = useState<string>( '' );
    const [ employeeId, setEmployeeId ] = useState<string>( '' );
    const [ firstName, setFirstName ] = useState<string>( '' );
    const [ lastName, setLastName ] = useState<string>( '' );
    const [ pageSize, setPageSize ] = useState<number>( 5 );
    const [ rows, setRows ] = useState<ResidentDto[ ] | null>( null );
    

    const { isLoading, searchByCardNumber, searchByEmployeeId, searchByName } = useSearchResidentHttp( );

    const { dispatch } = useContext( VspPortalContext );


    const cardNumberChangeHandler = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        setCardNumber( event.target.value.trim( ) );
        setEmployeeId( '' );
        setFirstName( '' );
        setLastName( '' );
        setCanSearch( event.target.value.trim( ).length > 2 ? true : false );

    };


    const cardNumberKeyPressHandler = async ( event: React.KeyboardEvent<HTMLDivElement> ) => {

        if( !canSearch || event.key !== 'Enter' ) {
            
            return;

        }

        const rows = await searchByCardNumber( asset, cardNumber );
        setRows( rows );
        
        setNewSearch( );

    };


    const employeeIdChangeHandler = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        setCardNumber( '' );
        setEmployeeId( event.target.value.trim( ) );
        setFirstName( '' );        
        setLastName( '' );
        setCanSearch( event.target.value.trim( ).length > 2 ? true : false );

    };


    const employeeIdKeyPressHandler = async ( event: React.KeyboardEvent<HTMLDivElement> ) => {

        if( !canSearch || event.key !== 'Enter' ) {
            
            return;

        }

        const rows = await searchByEmployeeId( asset, employeeId );
        setRows( rows );
        
        setNewSearch( );

    };


    const handleAssetChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        const asset = (event.target as HTMLInputElement).value as Asset
        setAsset( asset );

    };
    

    const handleFirstNameChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        setCardNumber( '' );
        setEmployeeId( '' );
        setFirstName( event.target.value.trimStart( ) );
        setCanSearch( ( event.target.value.trim( ).length > 1 && lastName.length > 1 ) ? true : false );

    };


    const handleFirstNameKeyPress = async ( event: React.KeyboardEvent<HTMLDivElement> ) => {

        if( !canSearch || event.key !== 'Enter' ) {
            
            return;

        }

        const rows = await searchByName( asset, firstName.trim( ), false, lastName.trim( ) );
        setRows( rows );
        
        setNewSearch( );

    };


    const handleResetClick = ( ) => {

        setCanSearch( false );
        setCardNumber( '' );
        setEmployeeId( '' );
        setFirstName( '' );
        setRows( null );
        setLastName( '' );
        onSelectResident( undefined );

    };


    const handleSearchPersonClick = async ( ) : Promise<void> => {

        let rows: ResidentDto[] = [];

        if( employeeId ) {

            rows = await searchByEmployeeId( asset, employeeId );

        } else if( cardNumber ) {

            rows = await searchByCardNumber( asset, cardNumber );

        } else if( lastName ) {

            rows = await searchByName( asset, firstName.trim( ), false, lastName.trim( ) );

        }

        setRows( rows );
        setNewSearch();

    }


    const handleSelectRow = ( employeeId : string ) => {
     
        if( !Boolean( employeeId ) ){

            
            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Cannot proceed without employee number' });
            onSelectResident( undefined );
            return;

        }

        onSelectResident( rows?.find( r => r.employeeId === employeeId ) );

    }


    const setNewSearch = () => {

        setButtonText( 'New Search' );
        onSelectResident( undefined );

    };


    const handleLastNameChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        setCardNumber( '' );
        setEmployeeId( '' );
        setLastName( event.target.value.trimStart( ) );
        setCanSearch( ( event.target.value.trim( ).length > 1 && firstName.length > 1 ) ? true : false );

    };


    const handleLastNameKeyPress = async ( event: React.KeyboardEvent<HTMLDivElement> ) => {

        if( !canSearch || event.key !== 'Enter' ) {
            
            return;

        }

        const rows = await searchByName( asset, firstName.trim( ), false, lastName.trim( ) );
        setRows( rows );
        
        setNewSearch( );

    };

  
    return (

        <>
      
            <Card sx={{ maxWidth: 'fit-content', boxShadow: 5 }}>

                <CardHeader 
                    sx={{ fontWeight: '600', fontSize: '20px', paddingTop: '5px', paddingBottom: '0' }}
                    title="BHP Employee/Contractor Search"
                    avatar={
                        <BadgeIcon />
                    }
                    action={
                        <Tooltip title="Reset">
                            <IconButton onClick={ handleResetClick }>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    }
                />

                <CardContent>
                    
                    <Stack direction="row" gap={0.5} display="flex" alignItems="center" >
                        
                        <FormControl 
                            sx={{ marginRight: '10px' }}
                            disabled={ isLoading || assets.length === 1 }
                        >
                            <FormLabel>Cardholder Asset</FormLabel>
                            <RadioGroup 
                                value={ asset }
                                onChange={ handleAssetChange }
                            >
                                
                                {
                                    assets.map( a => 
                                        <FormControlLabel key={ a } value={ a } control={ <Radio /> } label={ a } sx={{ marginBottom: '-5px', marginTop: '-10px' }} />
                                    )
                                }

                            </RadioGroup>
                        </FormControl>
                            
                        <TextField
                            sx={ textFieldSx } 
                            label="First Name" 
                            variant="outlined"
                            value={firstName}
                            onChange={ handleFirstNameChange }
                            onKeyPress={ handleFirstNameKeyPress }
                            disabled={isLoading}
                        />

                        <TextField 
                            sx={{ ...textFieldSx, marginLeft: '5px' }} 
                            label="Last Name" 
                            variant="outlined"
                            value={ lastName }
                            onChange={ handleLastNameChange }
                            onKeyPress={ handleLastNameKeyPress }
                            disabled={ isLoading} 
                        />

                        <Typography>
                            or
                        </Typography>
                        
                        <TextField 
                            sx={ textFieldSx } 
                            label="Employee ID" 
                            variant="outlined"
                            value={employeeId}
                            onChange={employeeIdChangeHandler}
                            onKeyPress={employeeIdKeyPressHandler}
                            disabled={isLoading}
                        />

                        <Typography>
                            or
                        </Typography>

                        <TextField 
                            sx={ textFieldSx } 
                            label="Card Number" 
                            variant="outlined"
                            value={cardNumber}
                            onChange={cardNumberChangeHandler}
                            onKeyPress={cardNumberKeyPressHandler}
                            disabled={isLoading}
                        />

                    </Stack>

                </CardContent>

                <CardActions
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end"
                    }}
                >

                    <LoadingButton
                        sx={{ fontWeight: 'bold', height: '55px', marginBottom: '10px', marginRight:'10px', textTransform: 'none', width: '150px' }}
                        loadingPosition="start"
                        startIcon={<PersonSearchIcon />}
                        variant="contained"
                        disabled={ !canSearch || isLoading }
                        onClick={ handleSearchPersonClick }
                        loading={isLoading}
                    >
                        { buttonText }
                    </LoadingButton>
                    
                </CardActions>

            </Card>  

            { 
                rows && rows.length > 0 && 

                <DataGrid
                    disableSelectionOnClick     
                    getRowId={ _ => Math.floor(Math.random() * 100_000_000) }
                    onRowClick={ ( params: GridRowParams, _, __ ) => handleSelectRow( params.row.employeeId )  }
                    pageSize={pageSize}
                    onPageSizeChange={ ( newPageSize ) => setPageSize( newPageSize ) }
                    rowsPerPageOptions={ [5, 10, 20] }
                    pagination
                    rows={ rows }
                    columns={ columns }
                    autoHeight={ true }
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'firstName', sort: 'asc' }],
                        }
                    }}
                    sx={{ ...dataGridSx, width: '1227px', boxShadow: 5, marginTop: '20px !important' }}
                />
            }

            {
                
                rows && rows.length === 0 && 
                <div style={{color:'red'}}>*No results found for the search criteria</div>

            }

        </>

    );
  
};
